import React from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Upload, 
  Avatar, 
  Row, 
  Col, 
  Typography,
  Divider,
  Space,
  Select
} from 'antd';
import { 
  UserOutlined, 
  PhoneOutlined, 
  GlobalOutlined,
  EnvironmentOutlined,
  CameraOutlined,
  MailOutlined
} from '@ant-design/icons';
import { MenteeProfileResponse, CategoryResponse } from '../../types';
import styles from '../../pages/Mentor/Panel/MentorPanel.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ProfileFormProps {
  form: any;
  profileData: MenteeProfileResponse | null;
  categories: CategoryResponse[];
  loading: boolean;
  saving: boolean;
  uploading: boolean;
  onSave: (values: any) => void;
  onImageUpload: (file: File) => boolean;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  form,
  profileData,
  categories,
  loading,
  saving,
  uploading,
  onSave,
  onImageUpload
}) => {
  return (
    <Form
        form={form}
        layout="vertical"
        onFinish={onSave}
        disabled={loading || saving}
      >
        {/* Profile Picture Section */}
        <div style={{ marginBottom: '32px' }}>
          <Text strong style={{ display: 'block', marginBottom: '12px', color: 'var(--text-primary)' }}>
            Profil Fotoğrafı
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Avatar 
              size={120} 
              style={{ 
                backgroundColor: 'var(--primary-100)', 
                color: 'var(--primary-600)',
                fontSize: '36px',
                fontWeight: 'bold'
              }}
              src={profileData?.profileImage ? `data:image/jpeg;base64,${profileData.profileImage}` : undefined}
            >
              {profileData ? `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}` : 'AY'}
            </Avatar>
            <div>
              <Upload
                name="avatar"
                listType="picture"
                showUploadList={false}
                beforeUpload={onImageUpload}
              >
                <Button 
                  icon={<CameraOutlined />}
                  loading={uploading}
                >
                  {uploading ? 'Yükleniyor...' : 'Fotoğraf Değiştir'}
                </Button>
              </Upload>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  JPG, PNG veya GIF (Max. 10MB)
                </Text>
              </div>
            </div>
          </div>
        </div>

        <Divider style={{ margin: '32px 0' }} />

        {/* Form Fields */}
        <div className={styles.formContainer}>
          <Row gutter={[24, 16]} className={styles.formRow}>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Adı"
                name="firstName"
                rules={[{ required: true, message: 'Adı gereklidir!' }]}
              >
                <Input 
                  prefix={<UserOutlined style={{ color: 'var(--primary-600)' }} />}
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Soyadı"
                name="lastName"
                rules={[{ required: true, message: 'Soyadı gereklidir!' }]}
              >
                <Input 
                  prefix={<UserOutlined style={{ color: 'var(--primary-600)' }} />}
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Giriş E-postası">
                <Input
                  value={profileData?.email || ''}
                  prefix={<MailOutlined style={{ color: 'var(--primary-600)' }} />}
                  disabled
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Telefon Numarası"
                name="phoneNumber"
              >
                <Input 
                  prefix={<PhoneOutlined style={{ color: 'var(--primary-600)' }} />}
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Web Sitesi Linki"
                name="websiteUrl"
              >
                <Input 
                  prefix={<GlobalOutlined style={{ color: 'var(--primary-600)' }} />}
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Şehir"
                name="city"
              >
                <Input 
                  prefix={<EnvironmentOutlined style={{ color: 'var(--primary-600)' }} />}
                  style={{ borderRadius: '8px', height: '44px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Ülke"
                name="country"
              >
                <Select 
                  style={{ borderRadius: '8px', height: '44px' }}
                  suffixIcon={<EnvironmentOutlined style={{ color: 'var(--primary-600)' }} />}
                >
                  <Option value="turkey">Türkiye</Option>
                  <Option value="usa">Amerika Birleşik Devletleri</Option>
                  <Option value="germany">Almanya</Option>
                  <Option value="uk">İngiltere</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                label="Hakkımda (Bio)"
                name="bio"
              >
                <TextArea 
                  rows={4}
                  placeholder="Kendiniz hakkında kısa bir açıklama yazın..."
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '32px 0' }} />

        <div style={{ textAlign: 'right', padding: '0 24px 24px 24px' }}>
          <Space>
            <Button 
              size="large"
            >
              İptal
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              size="large"
              loading={saving}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </Space>
        </div>
      </Form>
  );
};

export default ProfileForm;
